import type { Locale } from './i18n';

/*
 * METRIC INFO — the per-metric explainer registry behind every hoverable
 * metric name (table headers, KPI cards, trend cards). One entry per key in
 * the backend's METRIC_DEFS (apps/api/src/dashboard/metrics.ts), which is the
 * single source of truth for the math — the formulas here mirror
 * computeMetrics() verbatim. Surfaced through <MetricInfoTrigger>.
 *
 * Confidence = how literally to trust the number:
 *   high   — a direct count/sum from the source API (Meta, Momence, Stripe).
 *   medium — depends on a process (the staff check-in toggle), a fixed FX
 *            rate, or blended attribution (counts ALL gym sales/bookings,
 *            not just ad-sourced ones; first-month basis).
 *   low    — reserved for heuristics (none on the dashboard today).
 */

export type ConfidenceLevel = 'high' | 'medium' | 'low';
export type Verdict = 'good' | 'warn' | 'bad';

/** A directional benchmark band (industry heuristic, NOT a Meta-documented spec).
 * `good`/`warn`/`bad` are thresholds; `higherIsBetter` flips the comparison
 * (e.g. frequency: lower is better). `unit` drives display only. */
export interface BenchmarkBand {
  good: number;
  warn: number;
  bad: number;
  unit: 'pct' | 'ratio' | 'seconds';
  higherIsBetter: boolean;
}

export interface MetricInfoEntry {
  /** What the metric is, in plain language. */
  what: Record<Locale, string>;
  /** Compact formula, nb — e.g. 'klikk ÷ visninger'. */
  formula: string;
  /** Compact formula, en. */
  formulaEn: string;
  /** Which system + which exact data the number comes from. */
  source: Record<Locale, string>;
  confidence: ConfidenceLevel;
  /** One-line reason for the confidence score. */
  confidenceWhy: Record<Locale, string>;
  /** Optional directional benchmark band (engagement metrics). */
  benchmark?: BenchmarkBand;
  /** Optional actionable advice — what to do when weak vs strong. */
  advice?: Record<Locale, string>;
}

/** Map a confidence level to a Badge tone (high→good, medium→warn, low→bad). */
export function confidenceTone(c: ConfidenceLevel): Verdict {
  if (c === 'high') return 'good';
  if (c === 'medium') return 'warn';
  return 'bad';
}

/** Grade a value against a benchmark band → good/warn/bad (null if no value). */
export function evaluateBenchmark(
  value: number | null | undefined,
  b: BenchmarkBand,
): Verdict | null {
  if (value == null || !Number.isFinite(value)) return null;
  if (b.higherIsBetter) {
    if (value >= b.good) return 'good';
    if (value >= b.warn) return 'warn';
    return 'bad';
  }
  // lower is better (e.g. frequency, cost)
  if (value <= b.good) return 'good';
  if (value <= b.warn) return 'warn';
  return 'bad';
}

// --- Shared copy (one voice, reused verbatim across entries) ---

const WHY_DIRECT_META: Record<Locale, string> = {
  nb: 'Tellet direkte av Meta — ingen modellering hos oss.',
  en: 'Counted directly by Meta — no modelling on our side.',
};
const WHY_CHECKIN: Record<Locale, string> = {
  nb: 'Avhenger av at staben huker av oppmøte i Momence.',
  en: 'Depends on staff toggling check-in in Momence.',
};
const WHY_BLENDED: Record<Locale, string> = {
  nb: 'Blandet attribusjon — teller alle salg, ikke kun annonse-drevne; første måned.',
  en: 'Blended attribution — counts all sales, not just ad-sourced; first-month basis.',
};

const SRC_META: Record<Locale, string> = {
  nb: 'Meta Ads API (kontonivå-innsikt)',
  en: 'Meta Ads API (account-level insights)',
};
const SRC_MOMENCE_SALES: Record<Locale, string> = {
  nb: 'Momence-salg (saleDate)',
  en: 'Momence sales (saleDate)',
};
const SRC_MOMENCE_SCHEDULE: Record<Locale, string> = {
  nb: 'Momence-timeplan (innsjekk-bryter)',
  en: 'Momence schedule (check-in toggle)',
};
const SRC_STRIPE: Record<Locale, string> = {
  nb: 'Stripe charges (faktisk innbetalt)',
  en: 'Stripe charges (actual cash collected)',
};

// `leads` (METRIC_DEFS key) and `metaLeads` (raw-fact / AdsPage column key)
// are the same metric — one entry, two registry keys.
const LEADS_ENTRY: MetricInfoEntry = {
  what: {
    nb: 'Instant-form-leads fra Meta (deduplisert) — lead-kampanjens konverteringshendelse.',
    en: "Meta instant-form leads (deduplicated) — the lead campaign's conversion event.",
  },
  formula: 'antall instant-form-leads',
  formulaEn: 'instant-form lead count',
  source: {
    nb: 'Meta Ads API (lead-handlinger, deduplisert)',
    en: 'Meta Ads API (lead actions, deduplicated)',
  },
  confidence: 'high',
  confidenceWhy: WHY_DIRECT_META,
};

const ENTRIES = {
  adSpend: {
    what: {
      nb: 'Totalt annonseforbruk på Meta den dagen, rapportert i NOK. I en funnel-visning: kun den funnelens kampanjer.',
      en: "Total Meta ad spend that day, reported in NOK. In a funnel view: only that funnel's campaigns.",
    },
    formula: 'forbruk (AED) × 2,9',
    formulaEn: 'spend (AED) × 2.9',
    source: {
      nb: 'Meta Ads API (kontonivå-innsikt), omregnet AED→NOK',
      en: 'Meta Ads API (account-level insights), converted AED→NOK',
    },
    confidence: 'medium',
    confidenceWhy: {
      nb: 'Fast AED→NOK-kurs (2,9) — ikke dagskurs.',
      en: 'Fixed AED→NOK rate (2.9) — not the daily rate.',
    },
  },
  adSpendAed: {
    what: {
      nb: 'Annonseforbruket i native valuta (AED), slik Meta fakturerer kontoen.',
      en: 'Ad spend in the native currency (AED), as Meta bills the account.',
    },
    formula: 'sum dagsforbruk (AED)',
    formulaEn: 'daily spend total (AED)',
    source: SRC_META,
    confidence: 'high',
    confidenceWhy: {
      nb: 'Direkte fra Meta — ingen valutaomregning.',
      en: 'Straight from Meta — no currency conversion.',
    },
  },
  impressions: {
    what: {
      nb: 'Antall ganger annonsene ble vist.',
      en: 'Number of times your ads were shown.',
    },
    formula: 'sum visninger',
    formulaEn: 'impression count',
    source: SRC_META,
    confidence: 'high',
    confidenceWhy: WHY_DIRECT_META,
  },
  reach: {
    what: {
      nb: 'Unike personer som så annonsene minst én gang.',
      en: 'Unique people who saw your ads at least once.',
    },
    formula: 'unike personer nådd',
    formulaEn: 'unique people reached',
    source: SRC_META,
    confidence: 'high',
    confidenceWhy: {
      nb: 'Direkte fra Meta; unikhet på tvers av enheter er Metas eget estimat.',
      en: "Straight from Meta; cross-device uniqueness is Meta's own estimate.",
    },
  },
  cpm: {
    what: {
      nb: 'Kostnad per 1000 visninger — prisen på rekkevidde. Lavere = billigere.',
      en: 'Cost per 1,000 impressions — the price of reach. Lower = cheaper.',
    },
    formula: 'forbruk ÷ visninger × 1000',
    formulaEn: 'spend ÷ impressions × 1000',
    source: {
      nb: 'Avledet: Meta-forbruk ÷ Meta-visninger',
      en: 'Derived: Meta spend ÷ Meta impressions',
    },
    confidence: 'high',
    confidenceWhy: {
      nb: 'Begge tall direkte fra Meta; kun den faste NOK-kursen i omregningen.',
      en: 'Both inputs straight from Meta; only the fixed NOK rate applies.',
    },
  },
  clicks: {
    what: {
      nb: 'Lenkeklikk på annonsene (inline link clicks).',
      en: 'Link clicks on your ads (inline link clicks).',
    },
    formula: 'sum lenkeklikk',
    formulaEn: 'link-click count',
    source: SRC_META,
    confidence: 'high',
    confidenceWhy: WHY_DIRECT_META,
  },
  ctr: {
    what: {
      nb: 'Klikkrate — hvor fengende annonsen er. Fallende CTR betyr ofte kreativ slitasje.',
      en: 'Click-through rate — how compelling the creative is. Falling CTR usually means creative fatigue.',
    },
    formula: 'klikk ÷ visninger',
    formulaEn: 'clicks ÷ impressions',
    source: {
      nb: 'Avledet: Meta-klikk ÷ Meta-visninger',
      en: 'Derived: Meta clicks ÷ Meta impressions',
    },
    confidence: 'high',
    confidenceWhy: {
      nb: 'Ren Meta-ratio — begge tall direkte fra API-et.',
      en: 'Pure Meta ratio — both inputs straight from the API.',
    },
  },
  cpc: {
    what: {
      nb: 'Kostnad per lenkeklikk.',
      en: 'Cost per link click.',
    },
    formula: 'forbruk ÷ klikk',
    formulaEn: 'spend ÷ clicks',
    source: {
      nb: 'Avledet: Meta-forbruk ÷ Meta-klikk',
      en: 'Derived: Meta spend ÷ Meta clicks',
    },
    confidence: 'high',
    confidenceWhy: {
      nb: 'Begge tall direkte fra Meta; kun den faste NOK-kursen i omregningen.',
      en: 'Both inputs straight from Meta; only the fixed NOK rate applies.',
    },
  },
  leads: LEADS_ENTRY,
  metaLeads: LEADS_ENTRY,
  bookings: {
    what: {
      nb: 'Prøvetimer booket. Alle-visning: Momence-avtaler (hele studioet, inkl. drop-in). Booking-funnel: Metas schedule_website — matcher Ads Manager.',
      en: "Trial bookings. All view: Momence appointments (gym-wide, incl. walk-ins). Booking funnel: Meta's schedule_website — matches Ads Manager.",
    },
    formula: 'antall bookede prøvetimer',
    formulaEn: 'booked-trial count',
    source: {
      nb: 'Momence-avtaler (Alle) · Meta schedule_website (Booking-funnel)',
      en: 'Momence appointments (All) · Meta schedule_website (Booking funnel)',
    },
    confidence: 'high',
    confidenceWhy: {
      nb: 'Direkte telling i kildesystemet i begge visninger.',
      en: 'A direct count in the source system in both views.',
    },
  },
  lpPct: {
    what: {
      nb: 'Landing→booking-rate: hvor godt annonseklikk blir til bookede prøvetimer.',
      en: 'Landing→booking rate: how well ad clicks convert into booked trials.',
    },
    formula: 'bookinger ÷ klikk',
    formulaEn: 'bookings ÷ clicks',
    source: {
      nb: 'Avledet: Momence-bookinger ÷ Meta-klikk',
      en: 'Derived: Momence bookings ÷ Meta clicks',
    },
    confidence: 'medium',
    confidenceWhy: {
      nb: 'Blandet attribusjon — teller alle bookinger (inkl. drop-in), ikke kun annonse-drevne.',
      en: 'Blended attribution — counts all bookings (incl. walk-ins), not just ad-sourced.',
    },
  },
  cpl: {
    what: {
      nb: 'Kostnad per Meta-lead — lead-funnelens viktigste effektivitetstall.',
      en: "Cost per Meta lead — the lead funnel's headline efficiency number.",
    },
    formula: 'forbruk ÷ Meta-leads',
    formulaEn: 'spend ÷ Meta leads',
    source: {
      nb: 'Avledet: Meta-forbruk ÷ Meta-leads',
      en: 'Derived: Meta spend ÷ Meta leads',
    },
    confidence: 'high',
    confidenceWhy: {
      nb: 'Begge tall direkte fra Meta — samme tall som Ads Manager.',
      en: 'Both inputs straight from Meta — the same number as Ads Manager.',
    },
  },
  costPerBooking: {
    what: {
      nb: 'Kostnad per Meta-tilskrevet nettside-booking — booking-funnelens viktigste effektivitetstall.',
      en: "Cost per Meta-attributed website booking — the booking funnel's headline efficiency number.",
    },
    formula: 'forbruk ÷ Meta-bookinger',
    formulaEn: 'spend ÷ Meta bookings',
    source: {
      nb: 'Avledet: Meta-forbruk ÷ Meta schedule_website',
      en: 'Derived: Meta spend ÷ Meta schedule_website',
    },
    confidence: 'high',
    confidenceWhy: {
      nb: 'Meta-side hele veien — matcher Ads Manager.',
      en: 'Meta-side end to end — matches Ads Manager.',
    },
  },
  showedUp: {
    what: {
      nb: 'Personer som møtte opp til prøvetimen, kreditert til timedagen (dagen de møtte). Blandet — hele studioet.',
      en: 'People who attended their trial, credited to the appointment day (the day they showed up). Blended — gym-wide.',
    },
    formula: 'innsjekkede avtaler per timedag',
    formulaEn: 'checked-in appointments per appointment day',
    source: SRC_MOMENCE_SCHEDULE,
    confidence: 'medium',
    confidenceWhy: WHY_CHECKIN,
  },
  surPct: {
    what: {
      nb: 'Oppmøterate: andelen av dagens planlagte timer som faktisk møtte opp (ekte 0–100 %).',
      en: "Show-up rate: the share of that day's scheduled appointments that actually attended (true 0–100%).",
    },
    formula: 'oppmøtt ÷ planlagte timer samme dag',
    formulaEn: 'showed up ÷ appointments scheduled that day',
    source: SRC_MOMENCE_SCHEDULE,
    confidence: 'medium',
    confidenceWhy: WHY_CHECKIN,
  },
  surCost: {
    what: {
      nb: 'Kostnad per oppmøte — hva hvert fysiske besøk koster i annonsekroner.',
      en: 'Cost per show-up — what each in-person visit costs in ad spend.',
    },
    formula: 'forbruk ÷ oppmøtt',
    formulaEn: 'spend ÷ showed up',
    source: {
      nb: 'Avledet: Meta-forbruk ÷ Momence-innsjekk',
      en: 'Derived: Meta spend ÷ Momence check-ins',
    },
    confidence: 'medium',
    confidenceWhy: WHY_CHECKIN,
  },
  soldMemberships: {
    what: {
      nb: 'Nye betalte medlemskap solgt den dagen (medlemskaps-/abonnementssalg med verdi > 0). Blandet — hele studioet.',
      en: 'New paid memberships sold that day (membership/subscription sales with value > 0). Blended — gym-wide.',
    },
    formula: 'antall medlemskapssalg',
    formulaEn: 'membership-sale count',
    source: SRC_MOMENCE_SALES,
    confidence: 'high',
    confidenceWhy: {
      nb: 'Direkte salgstelling fra Momence.',
      en: 'A direct sales count from Momence.',
    },
  },
  ccPct: {
    what: {
      nb: 'Close-rate: andelen av de som møtte opp som kjøpte medlemskap.',
      en: 'Close conversion: the share of show-ups who bought a membership.',
    },
    formula: 'solgt ÷ oppmøtt',
    formulaEn: 'sold ÷ showed up',
    source: {
      nb: 'Avledet: Momence-salg ÷ Momence-innsjekk',
      en: 'Derived: Momence sales ÷ Momence check-ins',
    },
    confidence: 'medium',
    confidenceWhy: {
      nb: 'Blandet attribusjon — teller alle salg; nevneren avhenger av innsjekk-bryteren.',
      en: 'Blended attribution — counts all sales; the denominator depends on the check-in toggle.',
    },
  },
  lcPct: {
    what: {
      nb: 'Booking→medlem-rate: andelen av bookede prøvetimer som endte i medlemskap.',
      en: 'Booking→member rate: the share of booked trials that ended in a membership.',
    },
    formula: 'solgt ÷ bookinger',
    formulaEn: 'sold ÷ bookings',
    source: {
      nb: 'Avledet: Momence-salg ÷ Momence-bookinger',
      en: 'Derived: Momence sales ÷ Momence bookings',
    },
    confidence: 'medium',
    confidenceWhy: WHY_BLENDED,
  },
  revenue: {
    what: {
      nb: 'Medlemskapsomsetning bokført den dagen, netto etter refusjoner (NOK). Blandet — hele studioet.',
      en: 'Membership revenue booked that day, net of refunds (NOK). Blended — gym-wide.',
    },
    formula: 'omsetning − refusjoner',
    formulaEn: 'revenue − refunds',
    source: SRC_MOMENCE_SALES,
    confidence: 'high',
    confidenceWhy: {
      nb: 'Direkte fra Momence-salg, netto etter refusjoner.',
      en: 'Straight from Momence sales, net of refunds.',
    },
  },
  cash: {
    what: {
      nb: 'Faktisk innbetalt via Stripe den dagen (NOK). ≈ omsetning, siden medlemskap faktureres ved salg.',
      en: 'Actual cash collected via Stripe that day (NOK). ≈ revenue, since memberships bill at sale time.',
    },
    formula: 'sum Stripe-innbetalinger',
    formulaEn: 'Stripe charges total',
    source: SRC_STRIPE,
    confidence: 'high',
    confidenceWhy: {
      nb: 'Faktiske transaksjoner fra Stripe.',
      en: 'Actual transactions from Stripe.',
    },
  },
  cpa: {
    what: {
      nb: 'Kundeanskaffelseskost (CAC): annonsekost per solgt medlemskap.',
      en: 'Customer acquisition cost (CAC): ad spend per membership sold.',
    },
    formula: 'forbruk ÷ solgte medlemskap',
    formulaEn: 'spend ÷ memberships sold',
    source: {
      nb: 'Avledet: Meta-forbruk ÷ Momence-salg',
      en: 'Derived: Meta spend ÷ Momence sales',
    },
    confidence: 'medium',
    confidenceWhy: {
      nb: 'Blandet attribusjon — teller alle salg, ikke kun annonse-drevne; reell annonse-CPA er høyere.',
      en: 'Blended attribution — counts all sales, not just ad-sourced; true ad-only CPA is higher.',
    },
  },
  rPl: {
    what: {
      nb: 'Omsetnings-P/L: overskudd/underskudd målt mot bokført omsetning, første måned.',
      en: 'Revenue P&L: profit/loss against booked revenue, first-month basis.',
    },
    formula: 'omsetning − forbruk',
    formulaEn: 'revenue − spend',
    source: {
      nb: 'Avledet: Momence-omsetning − Meta-forbruk',
      en: 'Derived: Momence revenue − Meta spend',
    },
    confidence: 'medium',
    confidenceWhy: WHY_BLENDED,
  },
  cPl: {
    what: {
      nb: 'Kontant-P/L: faktisk innbetalt minus annonseforbruk.',
      en: 'Cash P&L: actual cash collected minus ad spend.',
    },
    formula: 'kontant − forbruk',
    formulaEn: 'cash − spend',
    source: {
      nb: 'Avledet: Stripe-kontant − Meta-forbruk',
      en: 'Derived: Stripe cash − Meta spend',
    },
    confidence: 'medium',
    confidenceWhy: WHY_BLENDED,
  },
  rRoi: {
    what: {
      nb: 'Omsetnings-ROI som multippel (2× = dobbelt tilbake). Første måned — medlemskap er gjentakende, så livstids-ROI er langt høyere.',
      en: 'Revenue ROI as a multiple (2× = double back). First-month — memberships recur, so lifetime ROI is far higher.',
    },
    formula: 'omsetning ÷ forbruk',
    formulaEn: 'revenue ÷ spend',
    source: {
      nb: 'Avledet: Momence-omsetning ÷ Meta-forbruk',
      en: 'Derived: Momence revenue ÷ Meta spend',
    },
    confidence: 'medium',
    confidenceWhy: WHY_BLENDED,
  },
  cRoi: {
    what: {
      nb: 'Kontant-ROI: innbetalt per annonsekrone.',
      en: 'Cash ROI: cash collected per ad krone.',
    },
    formula: 'kontant ÷ forbruk',
    formulaEn: 'cash ÷ spend',
    source: {
      nb: 'Avledet: Stripe-kontant ÷ Meta-forbruk',
      en: 'Derived: Stripe cash ÷ Meta spend',
    },
    confidence: 'medium',
    confidenceWhy: WHY_BLENDED,
  },
} satisfies Record<string, MetricInfoEntry>;

// --- Creative-engagement metrics (the ad-row dropdown diagnostics) ----------
// Formulas + Meta field names are verified; the benchmark bands are directional
// industry heuristics (a "rettesnor"), NOT a Meta-documented spec.

const SRC_META_AD: Record<Locale, string> = {
  nb: 'Meta Ads API (annonse-nivå innsikt)',
  en: 'Meta Ads API (ad-level insights)',
};
const WHY_FORMULA_BENCH: Record<Locale, string> = {
  nb: 'Formelen er bransjestandard; tersklene er en rettesnor, ikke en Meta-fasit.',
  en: 'Formula is industry-standard; the thresholds are a rule of thumb, not a Meta spec.',
};
const WHY_NO_NATIVE_BENCH: Record<Locale, string> = {
  nb: 'Verdi i NOK — vi fargelegger ikke mot en terskel (bransjetall er i USD).',
  en: 'Value in NOK — not graded against a threshold (industry numbers are in USD).',
};

const ENGAGEMENT_INFO = {
  hookRate: {
    what: {
      nb: 'Andelen visninger der noen så minst 3 sekunder — hvor godt åpningen stopper scrollingen.',
      en: 'Share of impressions watched ≥3 seconds — how well the opening stops the scroll.',
    },
    formula: '3s-spill ÷ visninger',
    formulaEn: '3-sec plays ÷ impressions',
    source: SRC_META_AD,
    confidence: 'high',
    confidenceWhy: WHY_FORMULA_BENCH,
    benchmark: { good: 30, warn: 22, bad: 15, unit: 'pct', higherIsBetter: true },
    advice: {
      nb: 'Svak (<15–22 %): problemet er de første 1–2 sekundene, ikke tilbudet. Klipp om åpningen — start med bevegelse/et ansikt/en tydelig påstand i bilde 1, kutt trege intro-er og logo. Test 3–4 nye kroker. Sterk (>30 %): kroken funker — skaler den og gjenbruk krok-strukturen; lekker det lenger nede, sitter problemet i kropp/tilbud, ikke kroken.',
      en: 'Weak (<15–22%): the first 1–2 seconds are the problem, not the offer. Re-cut the opener — lead with motion/a face/a bold claim in frame 1, kill slow intros and logos. Test 3–4 new hooks. Strong (>30%): the hook works — scale it and reuse the structure; if it leaks later, the body/offer is the issue, not the hook.',
    },
  },
  holdRate: {
    what: {
      nb: 'Av de som ble hektet (3s+), andelen som ble til ThruPlay — ren midt-i-videoen-retensjon.',
      en: 'Of those hooked (3s+), the share who reached ThruPlay — pure mid-video retention.',
    },
    formula: 'ThruPlays ÷ 3s-spill',
    formulaEn: 'ThruPlays ÷ 3-sec plays',
    source: SRC_META_AD,
    confidence: 'high',
    confidenceWhy: WHY_FORMULA_BENCH,
    benchmark: { good: 50, warn: 38, bad: 25, unit: 'pct', higherIsBetter: true },
    advice: {
      nb: 'Svak (<25–38 %): folk faller av etter kroken — midten drar ut. Stram tempoet, kom til verdien raskere, legg på undertekster og bytt scene/tekst hvert 2.–3. sekund, kort ned videoen. Sterk (>50 %): historien holder — push budsjett og sørg for at CTA-en på slutten er sterk nok.',
      en: 'Weak (<25–38%): people drop after the hook — the middle drags. Tighten pacing, reach the value faster, add captions and a scene/text cut every 2–3s, shorten it. Strong (>50%): the story holds — push budget and make the end CTA strong enough.',
    },
  },
  thruplayRate: {
    what: {
      nb: 'Andelen av alle visninger som ble en ThruPlay — krok + hold i ett tall.',
      en: 'Share of all impressions that became a ThruPlay — hook + hold in one number.',
    },
    formula: 'ThruPlays ÷ visninger',
    formulaEn: 'ThruPlays ÷ impressions',
    source: SRC_META_AD,
    confidence: 'medium',
    confidenceWhy: WHY_FORMULA_BENCH,
    benchmark: { good: 25, warn: 13, bad: 8, unit: 'pct', higherIsBetter: true },
    advice: {
      nb: 'Svak (<8–13 %): enten svikter kroken eller kroppen — splitt i krokrate (3s/visn.) og hold-rate (ThruPlay/3s) for å se hvilken halvdel som lekker, og fiks den. Sterk (>25 %): kreativen vinner dype visninger billig — sjekk kost/ThruPlay og skaler.',
      en: 'Weak (<8–13%): either the hook or the body fails — split into hook rate (3s/impr) and hold rate (ThruPlay/3s) to see which half leaks, then fix it. Strong (>25%): the creative earns deep views cheaply — check cost/ThruPlay and scale.',
    },
  },
  completionPct: {
    what: {
      nb: 'Andelen visninger der videoen ble sett helt til slutt — strengeste retensjonssignal.',
      en: 'Share of impressions watched to 100% — the strictest retention signal.',
    },
    formula: 'p100 ÷ visninger',
    formulaEn: 'p100 ÷ impressions',
    source: SRC_META_AD,
    confidence: 'medium',
    confidenceWhy: WHY_FORMULA_BENCH,
    benchmark: { good: 15, warn: 7, bad: 4, unit: 'pct', higherIsBetter: true },
    advice: {
      nb: 'Svak (<4–7 %): se retensjonskurven (p25→p100) og finn stupet — fall mellom p25 og p50 = for lang midt; nær p75 = poenget/CTA kommer for sent. Klipp til delen folk faktisk ser. Sterk (>15 %): hele videoen funker — flytt CTA tidligere og test en kortere variant.',
      en: 'Weak (<4–7%): read the retention curve (p25→p100) and find the cliff — drop between p25–p50 = the middle is too long; near p75 = the payoff/CTA lands too late. Trim to what people watch. Strong (>15%): the whole video works — move the CTA earlier and test a shorter cut.',
    },
  },
  avgWatchSec: {
    what: {
      nb: 'Snitt sekunder videoen ble sett per visning (Meta deler allerede på spill).',
      en: 'Average seconds the video was watched per impression (Meta already divides by plays).',
    },
    formula: 'snitt sett-tid (sek)',
    formulaEn: 'average watch time (sec)',
    source: SRC_META_AD,
    confidence: 'medium',
    confidenceWhy: WHY_FORMULA_BENCH,
    benchmark: { good: 10, warn: 5, bad: 3, unit: 'seconds', higherIsBetter: true },
    advice: {
      nb: 'Lav: oppmerksomheten dør tidlig — kort ned og flytt kjernebudskapet fremover (en 30s-annonse sett 4s bør være en 10s-annonse). Høy: seerne investerer tid — du har rom til mer bevis/historie før CTA. Sammenlign alltid normalisert (% sett) på tvers av ulik lengde.',
      en: 'Low: attention dies early — shorten and move the core message earlier (a 30s ad watched 4s should be a 10s ad). High: viewers invest time — room for more proof before the CTA. Always compare normalized (% watched) across different lengths.',
    },
  },
  engagementRate: {
    what: {
      nb: 'Andelen visninger som ga en interaksjon (reaksjon/kommentar/deling/klikk/lagring).',
      en: 'Share of impressions that produced any interaction (reaction/comment/share/click/save).',
    },
    formula: 'post-engasjement ÷ visninger',
    formulaEn: 'post engagements ÷ impressions',
    source: SRC_META_AD,
    confidence: 'medium',
    confidenceWhy: WHY_FORMULA_BENCH,
    benchmark: { good: 3, warn: 1, bad: 0.5, unit: 'pct', higherIsBetter: true },
    advice: {
      nb: 'Svak (<0,5–1 %): kreativen er livløs — legg på en krok som inviterer reaksjon (spørsmål, dristig påstand, gjenkjennelig smerte), og bruk UGC/testimonial som engasjerer mer enn polert merkevare. Sterk (>3 %): folk bryr seg — les kommentarene for språk/innvendinger til neste kreativ. NB: engasjement er et diagnosesignal, ikke et mål — vekt alltid mot CTR og kost/booking.',
      en: 'Weak (<0.5–1%): the creative is inert — add a hook that invites reaction (question, bold claim, relatable pain), and lean on UGC/testimonial over polished brand. Strong (>3%): people care — mine the comments for language/objections. Note: engagement is a diagnostic, not a goal — weigh it against CTR and cost/booking.',
    },
  },
  linkCtr: {
    what: {
      nb: 'Klikkrate som teller bare lenkeklikk til destinasjonen — det rene intensjonssignalet.',
      en: 'CTR counting only link clicks to the destination — the clean intent signal.',
    },
    formula: 'lenkeklikk ÷ visninger',
    formulaEn: 'link clicks ÷ impressions',
    source: SRC_META_AD,
    confidence: 'high',
    confidenceWhy: WHY_FORMULA_BENCH,
    benchmark: { good: 1.5, warn: 0.9, bad: 0.6, unit: 'pct', higherIsBetter: true },
    advice: {
      nb: 'Vurder alltid kreativen på lenke-/utgående-CTR, ikke total-CTR (den blåses opp av ikke-intensjonsklikk). Svak (<0,6–0,9 %): annonsen får oppmerksomhet, men ingen lyst til å handle — skjerp tilbudet og CTA, gjør verdien tydelig (gratis prøvetime, uten kort). Sterk: skaler og test nye varianter.',
      en: 'Always judge creative on link/outbound CTR, not all-CTR (it inflates with non-intent clicks). Weak (<0.6–0.9%): the ad gets attention but no desire to act — sharpen the offer + CTA, make the value explicit (free trial, no card). Strong: scale and test new variants.',
    },
  },
  outboundCtr: {
    what: {
      nb: 'Klikk som faktisk tar folk ut av Meta til landingssiden — beste prediktor for landingsside-økter.',
      en: 'Clicks that actually take people off Meta to the landing page — best predictor of LP sessions.',
    },
    formula: 'utgående klikk ÷ visninger',
    formulaEn: 'outbound clicks ÷ impressions',
    source: SRC_META_AD,
    confidence: 'medium',
    confidenceWhy: WHY_FORMULA_BENCH,
    benchmark: { good: 1.3, warn: 0.8, bad: 0.5, unit: 'pct', higherIsBetter: true },
    advice: {
      nb: 'Foretrekk utgående CTR når du sender trafikk til en ekstern landingsside — den utelukker app-interne klikk som aldri når siden. Et stort gap mellom lenke-CTR og utgående CTR betyr at folk klikker, men ikke lastes inn (treg side/feil lenke). Svak: samme fiks som lenke-CTR.',
      en: 'Prefer outbound CTR for external landing pages — it excludes in-app clicks that never reach your site. A big gap between link CTR and outbound CTR means people click but don’t load (slow page/bad link). Weak: same fixes as link CTR.',
    },
  },
  frequency: {
    what: {
      nb: 'Hvor mange ganger i snitt hver person så annonsen — et slitasjesignal (lavere er sunnere).',
      en: 'Average times each person saw the ad — a fatigue signal (lower is healthier).',
    },
    formula: 'visninger ÷ rekkevidde',
    formulaEn: 'impressions ÷ reach',
    source: SRC_META_AD,
    confidence: 'high',
    confidenceWhy: {
      nb: 'Native Meta-felt; over et datospenn er det omtrentlig (rekkevidde er ikke additiv på tvers av dager).',
      en: 'Native Meta field; over a date range it is approximate (reach is not additive across days).',
    },
    benchmark: { good: 2, warn: 2.8, bad: 3.5, unit: 'ratio', higherIsBetter: false },
    advice: {
      nb: 'Frekvens er kontekst, ikke et mål i seg selv — tolk den mot CTR og CPA. Stigende frekvens MED fallende CTR/krokrate og stigende CPM = slitasje: bytt kreativ (ny krok/vinkel/format), utvid målgruppen, eller trim budsjettet på den slitne annonsen. På et lite lokalt publikum kommer slitasjen raskt.',
      en: 'Frequency is context, not a goal — read it against CTR and CPA. Rising frequency WITH falling CTR/hook rate and rising CPM = fatigue: refresh the creative (new hook/angle/format), broaden the audience, or trim budget on the tired ad. On a small local audience, fatigue comes fast.',
    },
  },
  costPerThruplay: {
    what: {
      nb: 'Hva en ThruPlay koster — rangerer videoer på kostnadseffektiv dyp oppmerksomhet.',
      en: 'What a ThruPlay costs — ranks videos on cost-efficient deep attention.',
    },
    formula: 'forbruk ÷ ThruPlays',
    formulaEn: 'spend ÷ ThruPlays',
    source: SRC_META_AD,
    confidence: 'medium',
    confidenceWhy: WHY_NO_NATIVE_BENCH,
    advice: {
      nb: 'Høy kost/ThruPlay = enten svak retensjon (fiks kreativen — se hold-rate) eller dyr levering (høy CPM fra et smalt/konkurranseutsatt publikum — utvid eller bytt plassering til Reels/feed). Lav = kreativen leverer dype visninger billig; en god kandidat å skalere.',
      en: 'High cost/ThruPlay = either weak retention (fix the creative — see hold rate) or expensive delivery (high CPM from a narrow/competitive audience — broaden or shift placement to Reels/feed). Low = the creative delivers deep views cheaply; a strong candidate to scale.',
    },
  },
  costPer3s: {
    what: {
      nb: 'Hva et 3-sekunders spill koster — les sammen med krokraten.',
      en: 'What a 3-second play costs — read together with hook rate.',
    },
    formula: 'forbruk ÷ 3s-spill',
    formulaEn: 'spend ÷ 3-sec plays',
    source: SRC_META_AD,
    confidence: 'medium',
    confidenceWhy: WHY_NO_NATIVE_BENCH,
    advice: {
      nb: 'Høy kost/3s med en grei krokrate peker på dyr levering (CPM) — utvid publikum eller bytt plassering. Høy kost med lav krokrate peker på selve åpningen — klipp om kroken.',
      en: 'High cost/3s with a decent hook rate points at expensive delivery (CPM) — broaden the audience or shift placement. High cost with a low hook rate points at the opener — re-cut the hook.',
    },
  },
} satisfies Record<string, MetricInfoEntry>;

/**
 * Keyed by metric key (METRIC_DEFS) + the engagement keys, plus aliases for
 * column keys used elsewhere: `metaLeads` → leads, and the AdsPage AdRow column
 * keys `spend` → adSpend, `cpBooking` → costPerBooking.
 */
export const METRIC_INFO: Record<string, MetricInfoEntry> = {
  ...ENTRIES,
  ...ENGAGEMENT_INFO,
  spend: ENTRIES.adSpend,
  cpBooking: ENTRIES.costPerBooking,
};
